import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Star, Phone, Mail, MapPin, CheckCircle2, PaintBucket, Hammer, Shield, Clock, Award, Users, ArrowUp, Calendar, MessageCircle } from 'lucide-react';
import { mockReviews, services, serviceTypes, projectGallery } from '../mock';
import { toast } from 'sonner';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
    source: '',
    campaign: '',
    medium: '',
    referrer: ''
  });

  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showChat, setShowChat] = useState(false);

  // Capture lead source on component mount
  useEffect(() => {
    const captureLeadSource = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const referrer = document.referrer || 'Direct';
      
      // Capture UTM parameters
      const utmSource = urlParams.get('utm_source') || '';
      const utmCampaign = urlParams.get('utm_campaign') || '';
      const utmMedium = urlParams.get('utm_medium') || '';
      
      // Determine source
      let source = 'Direct';
      if (utmSource) {
        source = utmSource;
      } else if (referrer.includes('google')) {
        source = urlParams.get('gclid') ? 'Google Ads' : 'Google Organic';
      } else if (referrer.includes('facebook') || referrer.includes('fb')) {
        source = 'Facebook';
      } else if (referrer.includes('bing')) {
        source = 'Bing';
      } else if (referrer !== 'Direct') {
        source = 'Referral';
      }
      
      setFormData(prev => ({
        ...prev,
        source,
        campaign: utmCampaign,
        medium: utmMedium,
        referrer
      }));
    };
    
    captureLeadSource();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceTypeChange = (value) => {
    setFormData(prev => ({ ...prev, serviceType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.serviceType) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Submit to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        // Google Ads Conversion Tracking
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17346636132/QuoteSubmission',
            'value': 1.0,
            'currency': 'USD',
            'transaction_id': data.quote_id
          });
        }
        
        toast.success('Quote request submitted! We\'ll contact you within 24 hours.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          serviceType: '',
          message: '',
          source: formData.source,
          campaign: formData.campaign,
          medium: formData.medium,
          referrer: formData.referrer
        });
      } else {
        toast.error(data.message || 'Failed to submit quote. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote. Please try again later.');
    }
  };

  const scrollToQuote = () => {
    const formElement = document.querySelector('.quote-form-card');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header-nav">
        <div className="header-container">
          <div className="logo-section">
            <img 
              src="/gmt-logo.png" 
              alt="GMT Drywall & Painting Logo" 
              className="company-logo"
            />
          </div>
          <nav className="nav-links">
            <a href="#services" className="nav-link">Services</a>
            <a href="#quote-form" className="nav-link">Contact</a>
            <Button onClick={scrollToQuote} className="cta-button">
              Get Free Quote
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              GMT Drywall & Painting
            </h1>
            <p className="hero-subtitle">
              Your Satisfaction Is Our Happiness. Family-owned and proudly serving the Twin Cities and surrounding areas with expert drywall and painting services.
            </p>
            <div className="hero-features">
              <div className="hero-feature">
                <CheckCircle2 className="feature-icon" />
                <span>Insured</span>
              </div>
              <div className="hero-feature">
                <CheckCircle2 className="feature-icon" />
                <span>Free Estimates</span>
              </div>
              <div className="hero-feature">
                <CheckCircle2 className="feature-icon" />
                <span>Same-Day Response</span>
              </div>
            </div>
            <div className="hero-cta">
              <Button onClick={scrollToQuote} size="lg" className="primary-cta">
                Get Your Free Quote Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Drywall & Painting Services</h2>
            <p className="section-subtitle">
              Comprehensive drywall and painting solutions for residential and commercial projects in the Twin Cities area
            </p>
          </div>
          
          {/* Unified Carousel: 3 items on desktop, 1 item on mobile */}
          <div className="services-carousel-container">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="services-carousel"
            >
              <CarouselContent className="-ml-4">
                {services.map((service) => (
                  <CarouselItem key={service.id} className="pl-4 basis-full md:basis-1/3">
                    <Card className="service-card">
                      <CardContent className="service-content">
                        <h3 className="service-title">{service.title}</h3>
                        <p className="service-description">{service.description}</p>
                        <ul className="service-features">
                          {service.features.map((feature, index) => (
                            <li key={index} className="service-feature">
                              <CheckCircle2 className="check-icon" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="carousel-button-prev" />
              <CarouselNext className="carousel-button-next" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Combined Trust & Values Section */}
      <section className="py-12 bg-white">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-4 md:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-white rounded-lg md:rounded-xl shadow-md p-3 md:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <Shield className="w-6 h-6 md:w-9 md:h-9 mx-auto mb-1 md:mb-2 text-red-600" />
              <h4 className="text-[10px] md:text-base font-bold text-gray-800 mb-0 md:mb-1">Family Owned</h4>
              <p className="text-[10px] md:text-xs text-gray-600 hidden md:block">Trusted Business</p>
            </div>
            
            <div className="bg-white rounded-lg md:rounded-xl shadow-md p-3 md:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <Clock className="w-6 h-6 md:w-9 md:h-9 mx-auto mb-1 md:mb-2 text-red-600" />
              <h4 className="text-[10px] md:text-base font-bold text-gray-800 mb-0 md:mb-1">Experience</h4>
              <p className="text-[10px] md:text-xs text-gray-600 hidden md:block">Expert Craftsmen</p>
            </div>
            
            <div className="bg-white rounded-lg md:rounded-xl shadow-md p-3 md:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <Star className="w-6 h-6 md:w-9 md:h-9 mx-auto mb-1 md:mb-2 text-red-600" />
              <h4 className="text-[10px] md:text-base font-bold text-gray-800 mb-0 md:mb-1">High Quality</h4>
              <p className="text-[10px] md:text-xs text-gray-600 hidden md:block">Professional Work</p>
            </div>
            
            <div className="bg-white rounded-lg md:rounded-xl shadow-md p-3 md:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <PaintBucket className="w-6 h-6 md:w-9 md:h-9 mx-auto mb-1 md:mb-2 text-red-600" />
              <h4 className="text-[10px] md:text-base font-bold text-gray-800 mb-0 md:mb-1">Accountable</h4>
              <p className="text-[10px] md:text-xs text-gray-600 hidden md:block">Reliable Service</p>
            </div>
            
            <div className="bg-white rounded-lg md:rounded-xl shadow-md p-3 md:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <Award className="w-6 h-6 md:w-9 md:h-9 mx-auto mb-1 md:mb-2 text-red-600" />
              <h4 className="text-[10px] md:text-base font-bold text-gray-800 mb-0 md:mb-1">13+ Years</h4>
              <p className="text-[10px] md:text-xs text-gray-600 hidden md:block">In Business</p>
            </div>
            
            <div className="bg-white rounded-lg md:rounded-xl shadow-md p-3 md:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <Shield className="w-6 h-6 md:w-9 md:h-9 mx-auto mb-1 md:mb-2 text-red-600" />
              <h4 className="text-[10px] md:text-base font-bold text-gray-800 mb-0 md:mb-1">Insured</h4>
              <p className="text-[10px] md:text-xs text-gray-600 hidden md:block">Protected Service</p>
            </div>
            
            <div className="bg-white rounded-lg md:rounded-xl shadow-md p-3 md:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <Users className="w-6 h-6 md:w-9 md:h-9 mx-auto mb-1 md:mb-2 text-red-600" />
              <h4 className="text-[10px] md:text-base font-bold text-gray-800 mb-0 md:mb-1">500+ Clients</h4>
              <p className="text-[10px] md:text-xs text-gray-600 hidden md:block">Satisfied Customers</p>
            </div>
            
            <div className="bg-white rounded-lg md:rounded-xl shadow-md p-3 md:p-5 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <Star className="w-6 h-6 md:w-9 md:h-9 mx-auto mb-1 md:mb-2 text-red-600" />
              <h4 className="text-[10px] md:text-base font-bold text-gray-800 mb-0 md:mb-1">5.0 Rating</h4>
              <p className="text-[10px] md:text-xs text-gray-600 hidden md:block">Google Reviews</p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Gallery Section */}
      <section id="gallery" className="gallery-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Projects</h2>
            <p className="section-subtitle">
              Quality craftsmanship showcased through our completed drywall and painting projects
            </p>
          </div>
          <div className="reviews-carousel-container">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="reviews-carousel"
            >
              <CarouselContent>
                {projectGallery.map((project) => (
                  <CarouselItem key={project.id} className="carousel-item-review md:basis-1/2 lg:basis-1/3">
                    <div className="gallery-item-carousel">
                      <img 
                        src={project.url} 
                        alt={project.alt}
                        className="gallery-image"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="carousel-button-prev" />
              <CarouselNext className="carousel-button-next" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">
              Simple, straightforward process to get your project done right
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 text-center border-2 border-gray-200 hover:border-red-500 hover:-translate-y-2 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Contact Us</h3>
              <p className="text-gray-600 leading-relaxed">Call or fill out the quote form with your project details</p>
            </div>
            
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 text-center border-2 border-gray-200 hover:border-red-500 hover:-translate-y-2 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Free Estimate</h3>
              <p className="text-gray-600 leading-relaxed">We provide a detailed, no-obligation quote within 24 hours</p>
            </div>
            
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 text-center border-2 border-gray-200 hover:border-red-500 hover:-translate-y-2 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Schedule Work</h3>
              <p className="text-gray-600 leading-relaxed">Choose a convenient time that works for your schedule</p>
            </div>
            
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 text-center border-2 border-gray-200 hover:border-red-500 hover:-translate-y-2 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Expert Service</h3>
              <p className="text-gray-600 leading-relaxed">Our professional team completes your project on time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section id="reviews" className="reviews-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What Our Customers Say</h2>
            <div className="google-rating">
              <img 
                src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
                alt="Google" 
                className="google-logo"
              />
              <div className="rating-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="star-filled" />
                ))}
              </div>
              <span className="rating-text">5.0 Rating</span>
            </div>
          </div>
          <div className="reviews-carousel-container">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="reviews-carousel"
            >
              <CarouselContent>
                {mockReviews.map((review) => (
                  <CarouselItem key={review.id} className="carousel-item-review md:basis-1/2 lg:basis-1/3">
                    <Card className="review-card">
                      <CardContent className="review-content">
                        <div className="review-header">
                          <div className="reviewer-avatar">{review.avatar}</div>
                          <div className="reviewer-info">
                            <h4 className="reviewer-name">{review.name}</h4>
                            <div className="review-stars">
                              {[...Array(review.rating)].map((_, i) => (
                                <Star key={i} className="star-small" />
                              ))}
                            </div>
                          </div>
                          <span className="review-date">{review.date}</span>
                        </div>
                        <p className="review-text">{review.text}</p>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="carousel-button-prev" />
              <CarouselNext className="carousel-button-next" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" className="quote-section">
        <div className="container">
          <div className="quote-grid">
            <div className="quote-info">
              <h2 className="quote-title">Get Your Free Quote Today</h2>
              <p className="quote-description">
                Fill out the form and we'll get back to you within 24 hours with a detailed, no-obligation quote. Family-owned and operated, we treat every job like it's in our own home.
              </p>
              <div className="contact-info">
                <div className="contact-item">
                  <Phone className="contact-icon" />
                  <div>
                    <h4>Phone</h4>
                    <a href="tel:+17632227298" className="contact-link">
                      <p>(763) 222-7298</p>
                    </a>
                  </div>
                </div>
                <div className="contact-item">
                  <Mail className="contact-icon" />
                  <div>
                    <h4>Email</h4>
                    <p>gmtdrywall@gmail.com</p>
                  </div>
                </div>
                <div className="contact-item">
                  <MapPin className="contact-icon" />
                  <div>
                    <h4>Service Area</h4>
                    <p>Twin Cities & Surrounding Areas, MN</p>
                  </div>
                </div>
              </div>
            </div>
            <Card className="quote-form-card">
              <CardContent className="quote-form-content">
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Name *</label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email *</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Phone *</label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="serviceType" className="form-label">Service Type *</label>
                    <Select value={formData.serviceType} onValueChange={handleServiceTypeChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Project Details (Optional)</label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your project..."
                      rows={4}
                    />
                  </div>
                  <Button type="submit" className="submit-button" size="lg">
                    Request Free Quote
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <img 
                  src="/gmt-logo.png" 
                  alt="GMT Drywall & Painting Logo" 
                  className="footer-company-logo"
                />
              </div>
              <p className="footer-description">
                Family-owned and operated drywall and painting service proudly serving the Twin Cities and surrounding areas. Licensed, insured, and committed to excellence.
              </p>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li>Drywall Installation</li>
                <li>Drywall Finishing</li>
                <li>Texture Removal & Application</li>
                <li>Drywall Repairs</li>
                <li>Interior Painting</li>
                <li>Exterior Painting</li>
              </ul>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Contact</h4>
              <ul className="footer-links">
                <li>(763) 222-7298</li>
                <li>gmtdrywall@gmail.com</li>
                <li>Saint Paul, MN</li>
                <li>Twin Cities Area</li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 GMT Drywall & Painting. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button onClick={scrollToTop} className="back-to-top" aria-label="Back to top">
          <ArrowUp className="w-6 h-6" />
        </button>
      )}

      {/* Live Chat Widget */}
      <div className="fixed bottom-6 left-6 md:right-6 md:left-auto z-50">
        {showChat && (
          <div className="mb-4 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm">GMT Drywall</h3>
                  <p className="text-red-100 text-xs">We're online!</p>
                </div>
              </div>
              <button 
                onClick={() => setShowChat(false)}
                className="text-white hover:text-red-100 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-4 bg-gray-50 max-h-80 overflow-y-auto">
              <div className="bg-white rounded-lg p-3 shadow-sm mb-3">
                <p className="text-sm text-gray-800 mb-2">👋 Hi! How can we help you today?</p>
                <p className="text-xs text-gray-600">We typically respond within minutes!</p>
              </div>
              <div className="space-y-2">
                <a 
                  href="sms:+17632227298"
                  className="block bg-white hover:bg-gray-50 rounded-lg p-3 shadow-sm transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Chat Now</p>
                      <p className="text-xs text-gray-600">Send us a message</p>
                    </div>
                  </div>
                </a>
                <button 
                  onClick={scrollToQuote}
                  className="w-full bg-white hover:bg-gray-50 rounded-lg p-3 shadow-sm transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Fill Out Form</p>
                      <p className="text-xs text-gray-600">Get your free quote</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={() => setShowChat(!showChat)}
          className="w-16 h-16 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
          aria-label="Open chat"
        >
          {showChat ? (
            <span className="text-2xl">✕</span>
          ) : (
            <MessageCircle className="w-7 h-7" />
          )}
        </button>
      </div>
    </div>
  );
};

export default LandingPage;
